import { OAuthProvider, signInWithPopup, type UserCredential } from 'firebase/auth';
import { auth } from '../firebase';
import { translateCurrent } from '../i18n';
import { withPopupTimeout } from './oauthPopupTimeout';

/**
 * Web counterpart to appleSignIn.ts. Firebase's signInWithPopup handles the
 * whole Apple OAuth round trip against the SAME Firebase project/Apple
 * provider config native uses, resolving to the same Firebase user/
 * Firestore profile either way.
 *
 * IMPORTANT — this requires configuration OUTSIDE this repo before it will
 * actually work in a browser, none of which this code can do on its own:
 *   1. A "Sign in with Apple for Web" Services ID registered in the Apple
 *      Developer portal (separate from the iOS app's Bundle ID registration
 *      already in use) — with app.tonyte.app (and localhost, for local
 *      testing) added as an authorized domain/return URL.
 *   2. That Services ID, plus the Apple Team ID and a Sign in with Apple
 *      private key, entered into Firebase Console → Authentication →
 *      Sign-in method → Apple → "OAuth code flow configuration" (Firebase's
 *      Apple provider needs this to validate popup/redirect sign-ins — the
 *      native-only config already in place for the iOS app is not enough).
 * Until both are done, this will fail with a Firebase error such as
 * `auth/operation-not-allowed` or `auth/invalid-oauth-client-id` — that is
 * an external configuration gap, not a bug in this code.
 */
// `_t` is unused here — kept only so this matches appleSignIn.ts's (native)
// signature, since useAuth.ts calls `performAppleSignIn(t)` at one shared
// call site regardless of platform. useAuth() itself runs above
// <I18nProvider> in App.tsx, so the `t` it would pass down is always
// I18nContext's dictionary-less default stub, not a real translator (see
// useAuth.ts's getFirebaseErrorMessage for the full explanation) —
// translateCurrent() below reads the active-language dictionaries directly
// instead, so it resolves correctly regardless of where it's called from.
export async function performAppleSignIn(_t: (key: string) => string): Promise<UserCredential> {
  const provider = new OAuthProvider('apple.com');
  try {
    return await withPopupTimeout(signInWithPopup(auth, provider));
  } catch (error) {
    const code =
      typeof error === 'object' && error !== null && 'code' in error
        ? String((error as { code?: string }).code)
        : '';
    if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
      const cancelError = new Error(translateCurrent('auth.appleSignInCancelled'));
      (cancelError as Error & { code?: string }).code = '1001';
      throw cancelError;
    }
    throw error;
  }
}
